import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HelperCard = ({ helper, onQuickBook }) => {
  const navigate = useNavigate();

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available-now':
        return 'text-success';
      case 'available-soon':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getAvailabilityText = (status) => {
    switch (status) {
      case 'available-now':
        return 'Available Now';
      case 'available-soon':
        return 'Available Soon';
      default:
        return 'View Schedule';
    }
  };

  const handleViewProfile = () => {
    navigate('/helper-profile-management', { state: { helperId: helper?.id } });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <div className="relative">
            <Image
              src={helper?.profileImage}
              alt={helper?.profileImageAlt}
              className="w-24 h-24 rounded-lg object-cover"
            />
            {helper?.verified && (
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center border-2 border-card">
                <Icon name="BadgeCheck" size={16} className="text-primary-foreground" />
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {helper?.name}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name={helper?.type === 'licensed' ? 'GraduationCap' : 'Heart'} size={16} />
                <span>{helper?.typeLabel}</span>
              </div>
            </div>
            <div className={`flex items-center gap-2 text-sm font-medium ${getAvailabilityColor(helper?.availabilityStatus)}`}>
              <div className="w-2 h-2 rounded-full bg-current" />
              {getAvailabilityText(helper?.availabilityStatus)}
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {helper?.bio}
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            {helper?.languages?.map((lang, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs font-medium text-foreground"
              >
                <Icon name="Globe" size={12} />
                {lang}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {helper?.expertise?.map((topic, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
              >
                {topic}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <Icon name="MapPin" size={16} />
              <span>{helper?.location}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Star" size={16} className="text-warning fill-warning" />
              <span className="font-medium text-foreground">{helper?.rating}</span>
              <span>({helper?.reviewCount})</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Calendar" size={16} />
              <span>{helper?.sessionsCompleted} sessions</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="default"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => onQuickBook(helper)}
              disabled={helper?.availabilityStatus === 'unavailable'}
            >
              Book Session
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="User"
              iconPosition="left"
              onClick={handleViewProfile}
            >
              View Profile
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelperCard;